<script setup lang="ts">
/**
 * Which test methods a lab performs.
 *
 * Split out from LabDialog deliberately. Editing a lab's attributes is one
 * PATCH that either works or does not; the methods are N creates and M deletes
 * where a partial failure is possible. Keeping them apart means each dialog
 * owns one transaction and can report an honest result, instead of a single
 * "Save" that can half-succeed.
 *
 * This is not cosmetic data. lab_analysis_types is an inner join in the client
 * API's capacity query (Laboratory::Rules.base_query in emsl-api), so a lab
 * with no rows here is never returned to a client for any test method.
 */
import { computed, inject, onMounted, ref, type Ref } from 'vue';
import {
  analysisTypesApi,
  labAnalysisTypesApi,
  type AnalysisType,
  type Lab,
} from '@/services/api/emsl';

interface DialogRefValue {
  data?: { item?: Lab };
}

const dialogRef = inject<Ref<DialogRefValue | undefined>>('dialogRef');
const emit = defineEmits<{ (e: 'close', data?: unknown): void }>();

const lab = dialogRef?.value?.data?.item;

const analysisTypes = ref<AnalysisType[]>([]);
const selectedIds = ref<number[]>([]);
const loading = ref(true);
const loadError = ref(false);

const UNCATEGORISED = 'Uncategorised';

/** Grouped by category. Categories alphabetical, methods alphabetical within
 *  each; anything uncategorised sorts last. */
const grouped = computed(() => {
  const by = new Map<string, AnalysisType[]>();
  for (const at of analysisTypes.value) {
    const key = at.category ?? UNCATEGORISED;
    if (!by.has(key)) by.set(key, []);
    by.get(key)!.push(at);
  }
  return [...by.entries()]
    .sort(([a], [b]) => (a === UNCATEGORISED ? 1 : b === UNCATEGORISED ? -1 : a.localeCompare(b)))
    .map(([category, types]) => ({
      category,
      types: [...types].sort((a, b) => a.name.localeCompare(b.name)),
    }));
});

const selectedCount = computed(() => selectedIds.value.length);

onMounted(async () => {
  try {
    const [all, assigned] = await Promise.all([
      analysisTypesApi.index(),
      lab?.id ? labAnalysisTypesApi.index(lab.id) : Promise.resolve([]),
    ]);
    analysisTypes.value = all;
    selectedIds.value = assigned.map((r) => r.analysis_type_id);
  } catch {
    loadError.value = true;
  } finally {
    loading.value = false;
  }
});

function toggle(id: number, checked: boolean): void {
  if (checked) {
    if (!selectedIds.value.includes(id)) selectedIds.value = [...selectedIds.value, id];
  } else {
    selectedIds.value = selectedIds.value.filter((x) => x !== id);
  }
}

function toggleCategory(types: AnalysisType[], checked: boolean): void {
  const ids = types.map((t) => t.id);
  if (checked) {
    selectedIds.value = [...new Set([...selectedIds.value, ...ids])];
  } else {
    selectedIds.value = selectedIds.value.filter((x) => !ids.includes(x));
  }
}

function allChecked(types: AnalysisType[]): boolean {
  return types.length > 0 && types.every((t) => selectedIds.value.includes(t.id));
}

function submit(): void {
  // Never emit a selection that could not be loaded: an empty array is
  // indistinguishable from "the user cleared every method", and saving that
  // would silently withdraw the lab from every client search.
  if (loadError.value) {
    emit('close');
    return;
  }
  emit('close', { analysis_type_ids: [...selectedIds.value] });
}
</script>

<template>
  <form class="methods-dialog" @submit.prevent="submit">
    <header class="methods-dialog__head">
      <div>
        <span class="methods-dialog__lab">{{ lab?.name }}</span>
        <span class="methods-dialog__count">
          {{ selectedCount }} of {{ analysisTypes.length }} selected
        </span>
      </div>
      <button
        v-if="!loading && !loadError"
        type="button"
        class="methods-dialog__link"
        :disabled="selectedCount === 0"
        @click="selectedIds = []"
      >
        Clear all
      </button>
    </header>

    <p v-if="loading" class="methods-dialog__note">Loading test methods…</p>

    <p v-else-if="loadError" class="methods-dialog__note methods-dialog__note--warn">
      Could not load test methods. Nothing has been changed — close and try again.
    </p>

    <template v-else>
      <p v-if="selectedCount === 0" class="methods-dialog__note methods-dialog__note--warn">
        No test methods selected. This lab will not be offered to clients for any method
        until at least one is chosen.
      </p>

      <div class="methods-dialog__categories">
        <fieldset v-for="g in grouped" :key="g.category" class="methods-dialog__category">
          <legend>
            <label class="methods-dialog__check methods-dialog__check--all">
              <input
                type="checkbox"
                :checked="allChecked(g.types)"
                :aria-label="`Select all ${g.category}`"
                @change="toggleCategory(g.types, ($event.target as HTMLInputElement).checked)"
              />
              <span>{{ g.category }}</span>
            </label>
          </legend>
          <label v-for="at in g.types" :key="at.id" class="methods-dialog__check">
            <input
              type="checkbox"
              :value="at.id"
              :checked="selectedIds.includes(at.id)"
              @change="toggle(at.id, ($event.target as HTMLInputElement).checked)"
            />
            <span>{{ at.name }}</span>
          </label>
        </fieldset>
      </div>
    </template>

    <div class="methods-dialog__actions">
      <button type="submit" :disabled="loading || loadError">Save</button>
      <button type="button" @click="emit('close')">Cancel</button>
    </div>
  </form>
</template>

<style scoped>
.methods-dialog {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.25rem 0 0.5rem;
  min-width: 28rem;
}
.methods-dialog__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
}
.methods-dialog__lab {
  font-weight: 600;
  color: #374151;
  margin-right: 0.6rem;
}
.methods-dialog__count {
  font-size: 0.8125rem;
  color: #6b7280;
}
.methods-dialog__link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.8125rem;
  color: #2563eb;
  cursor: pointer;
}
.methods-dialog__link:disabled {
  color: #9ca3af;
  cursor: default;
}
.methods-dialog__link:not(:disabled):hover {
  text-decoration: underline;
}
.methods-dialog__note {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
}
.methods-dialog__note--warn {
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 0.375rem;
  padding: 0.4rem 0.55rem;
}
/* CSS columns rather than a grid: the categories are very uneven — MICRO 25
   and ASBESTOS 18 against CHEMISTRY 3 and INDUSTRIAL HYGIENE 4 — and grid cells
   size to the tallest row, stranding the small ones in whitespace. Columns let
   the short categories stack together. `break-inside: avoid` stops one
   splitting across a column break. */
.methods-dialog__categories {
  column-width: 16rem;
  column-gap: 0.75rem;
}
.methods-dialog__category {
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0.5rem 0.6rem 0.6rem;
  margin: 0 0 0.75rem;
  min-width: 0;
  break-inside: avoid;
  -webkit-column-break-inside: avoid;
}
.methods-dialog__category legend {
  padding: 0 0.25rem;
}
.methods-dialog__check {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  font-size: 0.8125rem;
  color: #374151;
  padding: 0.1rem 0;
  cursor: pointer;
}
.methods-dialog__check--all {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #4b5563;
}
.methods-dialog__check input {
  margin-top: 0.15rem;
  flex: none;
}
/* Method names run to ~50 characters, so they wrap inside the column instead of
   forcing it wider and breaking the packing. */
.methods-dialog__check span {
  min-width: 0;
  overflow-wrap: anywhere;
  line-height: 1.3;
}
.methods-dialog__actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
</style>
